import PropTypes from 'prop-types';
import CLColorPicker from './CLColorPicker';
import { visuallyHidden } from '@mui/utils';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from "@mui/icons-material/Search";
import CLTagsButtonGroup from './CLTagsButtonGroup';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation } from 'react-router-dom';
import "../../../assets/styles/containers/_clTagsSummary.scss";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Paper, Button, Box, Grid, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableRow, IconButton, TableHead, TableSortLabel } from '@mui/material';
import axios from 'axios';
import CLDeleteRatings from '../../components/CommentLibrary/CommentLibraryTags/CLDeleteRating';
import AddNewRating from '../../components/CommentLibrary/CommentLibraryTags/AddNewRating';
import AddGlobalRating from '../../components/TemplateEditors/TemplateModals/AddGlobalRating';
import EditGlobalRating from '../../components/TemplateEditors/TemplateModals/EditGlobalRating';
import EnableGlobalRating from '../../components/TemplateEditors/TemplateModals/EnableGlobalRating';
import PublicIcon from '@mui/icons-material/Public';
import Preloader from '../../helpers/Preloader';
import { GetRatingsList, GetTemplateList, createGlobalRating, enableGlobalRating, reorderRatingsList } from '../../store/action';
import { apiCall } from '../../utils/action';
import { scrollElementIntoContainerView } from '../../utils/services';
import toast from 'react-hot-toast';
import config_settings from '../../config/config';

let BASE_URL;

const origin = () => {
    let origin = window.location.origin.toLowerCase();
    if (origin.includes("https://")) origin = origin.replace("https://", "");
    else origin = origin.replace("http://", "");
    BASE_URL = config_settings[origin]?.SERVER_URL;
};

origin();

// react-beautiful-dnd's Droppable can register its ref one tick later than it needs to
// (most visible under React 18 StrictMode's double-effect mount), which throws
// "Invariant failed: Cannot collect without a droppable ref" if a drag starts before that
// ref is attached. Delaying the Droppable's mount by a frame is the standard workaround.
const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true));
        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, []);
    if (!enabled) return null;
    return <Droppable {...props}>{children}</Droppable>;
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index} // Hides the tab panel when it's not selected
            id={`simple-tabpanel-${index}`} // Generates a unique ID for each tab panel
            aria-labelledby={`simple-tab-${index}`} // The ID of the tab
            {...other} // Spreads any other properties
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography> {/* Renders the tab's content */}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node, // The content of the tab
    index: PropTypes.number.isRequired, // The index of the tab
    value: PropTypes.number.isRequired, // The selected tab
};

function getComparator(order, orderBy) {
    const descendingComparator = (a, b, orderBy) => {
        if (orderBy === "include_in_summary") {
            return a[orderBy] === b[orderBy] ? 0 : a[orderBy] ? -1 : 1;
        }
        if (orderBy === "comments_count") {
            if (a[orderBy] < b[orderBy]) {
                return -1;
            } else if (a[orderBy] > b[orderBy]) {
                return 1;
            }
            return 0;
        }
        if (typeof a[orderBy] === 'string') {
            const lowercaseA = a[orderBy]?.toLowerCase();
            const lowercaseB = b[orderBy]?.toLowerCase();
            if (lowercaseA < lowercaseB) {
                return -1;
            } else if (lowercaseA > lowercaseB) {
                return 1;
            }
        }
        if (a[orderBy] < b[orderBy]) {
            return -1;
        } else if (a[orderBy] > b[orderBy]) {
            return 1;
        }
        return 0;
    };
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Rating order is server state: dragging, editing and creating all persist it through
// /reorder-ratings, and GET /ratings returns the list in that order. It used to be re-ordered here
// from a `ratings_priority_<companyID>` list in localStorage as well, which meant the tab showed a
// stale client-local order instead of the API's — and a different order per browser and device.

const CLRating = (props) => {
    const { isHTCompany } = props;
    const dispatch = useDispatch();
    const [order, setOrder] = useState('asc'); // asc or desc order
    const [selected, setSelected] = useState([]); // selected rows
    const [orderBy, setOrderBy] = useState('calories'); // currently ordered by column
    const [showLoader, setShowLoader] = useState(false); // show loader indicator
    const [deletedTagId, setDeletedTagId] = useState(""); //id for delete tag
    const [commentListData, setCommentListData] = useState([]); // comment list data
    const [initialTableData, setInitialTableData] = useState([]); // initial table data to perform search
    const [openAddEditRatingModal, setOpenAddEditRatingModal] = useState(false);
    const [editData, setEditData] = useState(null);
    // The table body scrolls inside its container, so a newly added rating is scrolled to by id
    // once the row it belongs to has rendered.
    const tableContainerRef = useRef(null);
    const [scrollToRatingId, setScrollToRatingId] = useState(null);

    // Global-rating-enabled companies use the same Add/Edit rating dialogs as the template editor
    // (in "standalone" mode, since there's no single template to attach ratings into here).
    const templateList = useSelector((state) => state?.templates?.templateList);
    const isGlobalRatingEnabled = !!templateList?.is_global_rating_enabled;
    const [ratingName, setRatingName] = useState("");
    const [ratingNameError, setRatingNameError] = useState(false);
    const [definition, setDefinition] = useState("");
    const [ratingColor, setRatingColor] = useState("#E89623");
    const [newRatingSummary, setNewRatingSummary] = useState("0");
    const [enableGRModal, setEnableGRModal] = useState(false);
    const [enablingGR, setEnablingGR] = useState(false);

    useEffect(() => {
        if (templateList) return;
        dispatch(
            GetTemplateList({
                company_id: sessionStorage.getItem("company_id"),
                user_id: sessionStorage.getItem("user_id"),
                role_id: sessionStorage.getItem("role_id"),
            })
        );
    }, [templateList, dispatch]);

    const handleOpenAddEditRating = (row = null) => {
        setEditData(row); // null means add, otherwise edit
        if (row) {
            setRatingName(row.name || "");
            setDefinition(row.definition || "");
            setRatingColor(row.color || "#E89623");
            setNewRatingSummary(row.include_in_summary ? "1" : "0");
            setRatingNameError(false);
        }
        setOpenAddEditRatingModal(true);
    };

    const handleCloseAddEditRating = () => {
        setEditData(null);
        setOpenAddEditRatingModal(false);
    };
    const handleUpdateRating = async (id, updatedPayload) => {
        const companyID = sessionStorage.getItem("company_id");
        const token = sessionStorage.getItem("token");
        try {
            const res = await axios.put(`${BASE_URL}/ratings/${id}`, updatedPayload, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "company-key": companyID
                }
            });
            if (res?.data?.success) {
                // Bubble the edited item to the top, shifting previously edited items down, and
                // persist that order so the next GET /ratings returns the same list.
                const edited = commentListData.find((r) => r.id === id);
                const updatedData = commentListData.map((r) => (r.id === id ? { ...r, ...updatedPayload } : r));
                const reordered = edited
                    ? [
                        { ...edited, ...updatedPayload },
                        ...updatedData.filter((r) => r.id !== id),
                    ]
                    : updatedData;
                dispatch(reorderRatingsList({ reordered_ratings: reordered.map((r) => r.id) })).catch((error) => console.error("Error persisting rating order:", error));
                setCommentListData(reordered);
                setInitialTableData(reordered);
                toast.success(res?.data?.message || "Rating has been updated");
            } else {
                console.error("Update failed:", res?.data?.message);
                toast.error(res?.data?.message || "Failed to update rating");
            }
        } catch (error) {
            console.error("Error updating rating:", error);
            toast.error(error?.response?.data?.message || "Something went wrong while updating the rating.");
        }
    };

    // The create response doesn't always carry the new rating's id, so fall back to matching it by
    // name in the freshly fetched list — without an id there is no row to scroll to.
    // Toasted here rather than in the dialogs so both of them — "Add rating" and the edit dialog's
    // "Save as new" — report a creation exactly once.
    const handleGlobalRatingCreated = async (newRating) => {
        toast.success("Rating has been added");
        const ratings = await fetchRatings();
        const name = (newRating?.name || "").trim().toLowerCase();
        const createdId =
            parseInt(newRating?.id) ||
            (name && ratings.find((rating) => (rating?.name || "").trim().toLowerCase() === name)?.id);
        if (createdId) setScrollToRatingId(createdId);
    };

    const handleUpdateGlobalRatingStandalone = (updatedName, updatedDefinition) => {
        const trimmedName = (updatedName || "").trim();
        if (!trimmedName) {
            setRatingNameError(true);
            return;
        }
        setRatingNameError(false);
        const payload = {
            name: trimmedName,
            color: ratingColor,
            definition: (updatedDefinition || "").trim(),
            include_in_summary: parseInt(newRatingSummary) === 1 ? 1 : 0,
        };
        handleUpdateRating(editData.id, payload).then(() => {
            handleCloseAddEditRating();
        });
    };

    // "Save as new" forks the edited fields into a brand new global rating, leaving the
    // rating being edited untouched.
    const handleSaveGlobalRatingAsNewStandalone = async (newName, newDefinition) => {
        const trimmedName = (newName || "").trim();
        if (!trimmedName) {
            setRatingNameError(true);
            return;
        }
        setRatingNameError(false);
        try {
            const result = await dispatch(
                createGlobalRating({
                    name: trimmedName,
                    color: ratingColor,
                    definition: (newDefinition || "").trim(),
                    include_in_summary: parseInt(newRatingSummary) === 1 ? 1 : 0,
                })
            );
            handleGlobalRatingCreated({
                id: parseInt(result?.data?.id),
                name: trimmedName,
                definition: (newDefinition || "").trim(),
                inSummary: parseInt(newRatingSummary) === 1,
                color: ratingColor,
            });
            handleCloseAddEditRating();
        } catch (err) {
            toast.error(err.message || "Failed to create rating");
        }
    };

    const fetchRatings = async () => {
        setShowLoader(true);
        try {
            const response = await dispatch(GetRatingsList());
            const data = response?.data?.data || [];
            setCommentListData(data);
            setInitialTableData(data);
            return data;
        } catch (err) {
            console.error("Ratings API error:", err);
            return [];
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        fetchRatings();
    }, []);

    // One-time conversion: migrate every non-PDF / non-narrative / non-Texas template's ratings into
    // the global ratings pool, then flip the company-level flag. Texas templates keep the fixed
    // rating set their TREC form requires, so they're left out of the migration entirely.
    const enableGlobalRatingForTemplate = (template) => {
        return new Promise((resolve, reject) => {
            apiCall(
                (response) => {
                    if (response?.status === 204) {
                        resolve();
                        return;
                    }
                    const success = response?.data?.success;
                    const message = response?.data?.message;
                    if (!success) {
                        toast.error(message || "Something went wrong while enabling global ratings.");
                        reject(new Error(message || "MIGRATE_RATINGS failed"));
                        return;
                    }
                    resolve();
                },
                { template_guid: template.guid, template_id: `${template.id}` },
                "MIGRATE_RATINGS"
            );
        });
    };

    const useGlobalRatings = async () => {
        if (!Array.isArray(templateList?.data)) {
            return;
        }
        const reversedTemplateList = [...templateList?.data].reverse();
        setEnablingGR(true);
        try {
            for (const template of reversedTemplateList || []) {
                if (!template?.is_pdf_template && !template?.is_narrative_template && !template?.is_texas) {
                    await enableGlobalRatingForTemplate(template);
                }
            }
            await dispatch(enableGlobalRating());
            await fetchRatings();
        } catch (error) {
            toast.error("Something went wrong while enabling global ratings.");
        } finally {
            setEnablingGR(false);
            setEnableGRModal(false);
        }
    };

    const location = useLocation();
    const handleopenAddNewTagModal = () => {
        if (!isHTCompany) return;
        setopenAddNewTagModal(true);
    } //modal toggle 
    const [openAddNewTagModal, setopenAddNewTagModal] = useState(false);
    const handleCloseAddNewTagModal = () => setopenAddNewTagModal(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [commentListInititalData, setCommentListInitialData] = useState([]); // initial comment list data
    const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
    const [editingRowId, setEditingRowId] = useState(null);
    const [editedName, setEditedName] = useState('');
    const handleOpenConfirmationModal = (row) => { setDeleteConfirmationModal(true); setDeletedTagId(row?.id); }
    const handleCloseConfirmationModal = () => setDeleteConfirmationModal(false);
    const handleSearchFilter = (query) => {
        const searchTerm = query.trim().toLowerCase();
        if (!searchTerm) {
            setCommentListData(initialTableData);
            return;
        }
        const filteredData = initialTableData.filter((item) => {
            const nameMatch = item?.name?.toLowerCase().includes(searchTerm);
            const definitionMatch = item?.definition?.toLowerCase().includes(searchTerm);
            const colorMatch = item?.color?.toLowerCase().includes(searchTerm);
            return nameMatch || definitionMatch || colorMatch;
        });
        setCommentListData(filteredData);
    };

    const EnhancedTableHead = (props) => {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
            props;
        const createSortHandler = (property) => (event) => {
            onRequestSort(event, property);
        };
        return (
            <TableHead>
                <TableRow>
                    {headCells?.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={'left'}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                            className='cl-rating-table-head'
                            style={{ width: headCell.width ? headCell.width : "" }}
                        >
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                                IconComponent={() =>
                                    (headCell.id !== "order" && headCell.id !== "global") ? (
                                        <UnfoldMoreIcon className='cl-sort-icon' />
                                    ) : (
                                        ""
                                    )
                                }
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }

    EnhancedTableHead.propTypes = {
        numSelected: PropTypes.number.isRequired,
        onRequestSort: PropTypes.func.isRequired,
        onSelectAllClick: PropTypes.func.isRequired,
        order: PropTypes.oneOf(['asc', 'desc']).isRequired,
        orderBy: PropTypes.string.isRequired,
        rowCount: PropTypes.number.isRequired,
    };

    const headCells = [
        {
            id: 'order',
            numeric: false,
            disablePadding: false,
            label: "  ",
            width: location.search !== "?zero-state" ? 110 : 55
        },
        {
            id: 'name',
            numeric: false,
            disablePadding: true,
            label: 'Name',
            width: location.search !== "?zero-state" ? 610 : 1010
        },
        {
            id: 'definition',
            numeric: false,
            disablePadding: true,
            label: 'Definition',
            width: location.search !== "?zero-state" ? 610 : 1010
        },
        {
            id: 'include_in_summary',
            numeric: false,
            disablePadding: false,
            label: 'In summary',
            width: location.search !== "?zero-state" ? 110 : 180
        },
        {
            id: 'color',
            numeric: false,
            disablePadding: false,
            label: 'Color',
            width: location.search !== "?zero-state" ? 110 : 180
        },
        {
            id: 'comments_count',
            numeric: false,
            disablePadding: false,
            label: 'Times used',
            width: location.search !== "?zero-state" ? 210 : 150
        },
        {
            id: 'global',
            numeric: false,
            disablePadding: false,
            label: '',
        },
    ];

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const selectedComment = commentListData?.map((n) => n.id);
            setSelected(selectedComment);
            return;
        }
        setSelected([]);
    };
    const isSelected = (id) => selected.indexOf(id) !== -1;

    // With no column sort active the comparator treats every row as equal, so the rows keep the
    // order the API returned.
    const visibleRows = useMemo(() => {
        const dataToSort = commentListData.length > 0 ? commentListData : initialTableData;
        return [...dataToSort].sort(getComparator(order, orderBy));
    }, [commentListData, order, orderBy, initialTableData]);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };
    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }
        const previousCommentListData = commentListData;
        const previousInitialTableData = initialTableData;
        // Reorder the rendered rows (visibleRows), not the raw commentListData — their order can
        // differ (column sort, priority bubbling), and result.source/destination indices are
        // relative to what's actually on screen.
        const items = reorder(
            visibleRows,
            result.source.index,
            result.destination.index
        );
        items.forEach((element, i) => {
            element.order = i + 1;
            return element;
        });
        const orderedIds = items.map((item) => item.id);
        setCommentListData(items);
        setInitialTableData(items);
        dispatch(reorderRatingsList({ reordered_ratings: orderedIds })).catch((error) => {
            setCommentListData(previousCommentListData);
            setInitialTableData(previousInitialTableData);
            toast.error(error.message || "Failed to reorder ratings");
        });
    };

    // Scrolls a just-added rating's row into view. Keyed off visibleRows so it waits for the row to
    // render (the global flow refetches the list first), and offsets the table head so the row
    // lands just below it however long the list is.
    useEffect(() => {
        if (!scrollToRatingId) return;
        let innerFrame = null;
        // Two frames, not one: adding a rating re-renders every row (each one a Draggable) and
        // react-beautiful-dnd remounts its Droppable, so measuring and animating in the same frame
        // as that work stutters. Waiting for the next paint lets the scroll run on a settled list.
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                const container = tableContainerRef.current;
                const row = container?.querySelector(`[data-rating-id="${scrollToRatingId}"]`);
                if (!row) return;
                const tableHead = container?.querySelector("thead");
                scrollElementIntoContainerView(container, row, tableHead?.offsetHeight || 0);
                setScrollToRatingId(null);
            });
        });
        return () => {
            cancelAnimationFrame(outerFrame);
            if (innerFrame) cancelAnimationFrame(innerFrame);
        };
    }, [scrollToRatingId, visibleRows]);

    const handleColorChange = (id, color) => {
        const updatedRows = commentListData.map((row) =>
            row.id === id ? { ...row, color: color } : row
        );
        setCommentListData(updatedRows);
    };

    const handleSaveEditedName = async (row) => {
        const trimmedName = editedName.trim();
        if (trimmedName === row.name.trim()) {
            setEditingRowId(null);
            return;
        }
        try {
            const companyID = sessionStorage.getItem("company_id");
            const token = sessionStorage.getItem("token");

            const payload = {
                name: trimmedName,
                color: row.color,
                definition: row.definition,
                include_in_summary: row.include_in_summary
            };
            await axios.put(`${BASE_URL}/ratings/${row.id}`, payload, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "company-key": companyID
                }
            });
            const updatedData = commentListData.map((item) =>
                item.id === row.id ? { ...item, name: trimmedName } : item
            );

            setCommentListData(updatedData);
            setEditingRowId(null);
        } catch (error) {
            console.error("Error updating rating name:", error);
            setEditingRowId(null);
        }
    };

    const handleAddNewRating = async (newTagData) => {
        const companyID = sessionStorage.getItem("company_id");
        const token = sessionStorage.getItem("token");

        const payload = {
            name: newTagData.name,
            color: newTagData.color,
            definition: newTagData.definition,
            include_in_summary: newTagData.include_in_summary,
        };

        try {
            const res = await axios.post(`${BASE_URL}/ratings`, payload, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "company-key": companyID
                }
            });

            if (res?.data?.success) {
                toast.success(res?.data?.message || "Rating has been added");
                const responseData = res?.data?.data;
                const newId = responseData?.id;
                if (newId) {
                    // Fast path: API returned the new item's id
                    const newRating = {
                        comments_count: 0,
                        ...responseData,
                        id: newId,
                        name: payload.name,
                        color: payload.color,
                        definition: payload.definition,
                        include_in_summary: payload.include_in_summary,
                    };
                    // Shown at the top, and that order is persisted so the next fetch matches.
                    const updated = [newRating, ...commentListData];
                    dispatch(reorderRatingsList({ reordered_ratings: updated.map((r) => r.id) })).catch((error) => console.error("Error persisting rating order:", error));
                    setCommentListData(updated);
                    setInitialTableData(updated);
                    setScrollToRatingId(newId);
                } else {
                    // Fallback: the API didn't return the new id, so refetch and take its order.
                    const refetchRes = await axios.get(`${BASE_URL}/ratings`, {
                        headers: { "Authorization": `Bearer ${token}`, "company-key": companyID },
                    });
                    const freshData = refetchRes?.data?.data || [];
                    setCommentListData(freshData);
                    setInitialTableData(freshData);
                    const created = freshData.find(
                        (rating) => (rating?.name || "").trim().toLowerCase() === payload.name.trim().toLowerCase()
                    );
                    if (created?.id) setScrollToRatingId(created.id);
                }
            } else {
                toast.error(res?.data?.message || "Failed to add rating");
            }
        } catch (error) {
            console.error("Error creating new rating:", error);
            toast.error(error?.response?.data?.message || "Something went wrong while adding the rating.");
        }
    };

    return (
        <>
            {isGlobalRatingEnabled ? (
                <>
                    <AddGlobalRating
                        open={openAddEditRatingModal && !editData}
                        onClose={handleCloseAddEditRating}
                        standalone
                        existingRatings={commentListData}
                        onCreated={handleGlobalRatingCreated}
                    />
                    <EditGlobalRating
                        open={openAddEditRatingModal && !!editData}
                        onClose={handleCloseAddEditRating}
                        standalone
                        ratingName={ratingName}
                        setRatingName={setRatingName}
                        definition={definition}
                        setDefinition={setDefinition}
                        ratingColor={ratingColor}
                        setRatingColor={setRatingColor}
                        newRatingSummary={newRatingSummary}
                        setNewRatingSummary={setNewRatingSummary}
                        isDefaultTemplate={false}
                        setIsDefaultTemplate={() => {}}
                        onUpdate={handleUpdateGlobalRatingStandalone}
                        onSaveAsNew={handleSaveGlobalRatingAsNewStandalone}
                        ratingNameError={ratingNameError}
                    />
                </>
            ) : (
                <AddNewRating
                    open={openAddEditRatingModal}
                    handleClose={handleCloseAddEditRating}
                    addNewRatingToList={handleAddNewRating}
                    editData={editData}
                    onSaveEdit={handleUpdateRating}
                    existingRatings={commentListData}
                />
            )}
            <Preloader showPreloader={enablingGR} />
            <EnableGlobalRating open={enableGRModal} onSubmit={useGlobalRatings} onClose={() => setEnableGRModal(false)} />
            {/* <Preloader showPreloader={showLoader} /> */}
            {/* <AddNewTag open={openAddNewTagModal} handleClose={handleCloseAddNewTagModal} setopenAddNewTagModal={setopenAddNewTagModal} GetCommentLibTagList={GetCommentLibTagList} /> */}
            {/* <CLRatingConfirmDelete
                setDeleteLoader={setDeleteLoader}
                deleteLoader={deleteLoader}
                handleCloseConfirmationModal={handleCloseConfirmationModal}
                deleteConfirmationModal={deleteConfirmationModal}
                deletedTagId={deletedTagId}
                setDeletedTagId={setDeletedTagId}
                GetCommentLibTagList={() => { }} // You can pass a real API function here later
            /> */}

            {/* <AddEditRating open={openAddEditRatingModal}
                handleClose={handleCloseAddEditRating}
                editData={editData}
                GetCLSummaryList={() => { }} /> */}
            <Box sx={{ width: '100%', padding: "30px" }}>
                <Grid container className='cl-search-container'>
                    <Grid item xs={6} md={6} lg={2} className='cl-search-bar'>
                        <div className="cl-search">
                            <TextField
                                size="small"
                                id="search-bar"
                                variant="outlined"
                                placeholder="Search in table"
                                onChange={(e) => handleSearchFilter(e.target.value)}
                            />
                            <SearchIcon className="cl-search-icon" />
                        </div>
                    </Grid>
                    <Grid item xs={6} md={6} lg={10} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
                        {!isGlobalRatingEnabled ? (
                            <Button
                                startIcon={<PublicIcon />}
                                sx={{ "& .MuiButton-startIcon": { marginRight: 0 } }}
                                variant="outlined"
                                className="global-ratings-btn"
                                onClick={() => setEnableGRModal(true)}
                            >
                                Use global ratings
                            </Button>
                        ) : null}
                        <Button startIcon={<AddIcon />} className='add-new-btn' onClick={() => { setEditData(null); setOpenAddEditRatingModal(true); }}>
                            <span>Add new</span>
                        </Button>
                    </Grid>
                </Grid>
                <Paper sx={{ width: '100%', mb: 2, border: "1px solid #D5D5D5", borderRadius: "3px" }} className='cl-table' elevation={0}>
                    <TableContainer
                        ref={tableContainerRef}
                        component={Paper}
                        className='cl-table-container'
                        style={{ overflowY: visibleRows?.length > 0 ? "auto" : "hidden !important" }}
                    >
                        <Table aria-label="simple table">
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                numSelected={selected.length}
                                onRequestSort={handleRequestSort}
                                rowCount={commentListData.length}
                                onSelectAllClick={handleSelectAllClick}
                            />
                            <DragDropContext onDragEnd={onDragEnd}>
                                <StrictModeDroppable droppableId="droppable">
                                    {(provided) => (
                                        <TableBody
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {location.search !== "?zero-state" && visibleRows?.length > 0 ? visibleRows.map((row, index) => {
                                                const isItemSelected = isSelected(row.id);
                                                const labelId = `enhanced-table-checkbox-${index}`;
                                                return (
                                                    <Draggable key={row.id} draggableId={String(row.id)} index={index}>
                                                        {(draggableProvided) => (
                                                    <TableRow
                                                        hover
                                                        tabIndex={-1}
                                                        ref={draggableProvided.innerRef}
                                                        selected={isItemSelected}
                                                        data-rating-id={row.id}
                                                        sx={{ cursor: 'pointer', height: "60px" }}
                                                        {...draggableProvided.draggableProps}
                                                        aria-checked={isItemSelected}
                                                    >
                                                        <TableCell
                                                            component="th"
                                                            id={labelId}
                                                            scope="row"
                                                            size="small"
                                                            padding="none"
                                                            sx={{ padding: "10px 20px 0px 20px" }}
                                                            className='cl-row-status cl-exp-col'
                                                        >
                                                            <IconButton {...draggableProvided.dragHandleProps}>
                                                                <DragIndicatorIcon
                                                                    sx={{ color: '#878787', mt: 1.2 }}
                                                                    fontSize="small"
                                                                />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            padding="none"
                                                            className='cl-name-col'
                                                        >
                                                            <p className='cl-rating-col-name'>{row.name}</p>
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            padding="none"
                                                            className='cl-defination-col'
                                                        >
                                                            <p className='cl-rating-col-name' style={{ width: "400px" }}>{row.definition}</p>
                                                        </TableCell>
                                                        <TableCell sx={{ width: "200px" }} align='center'>
                                                            {row?.include_in_summary ? <CheckIcon /> : ""}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{ padding: "10px 10px 10px 10px", alignContent: "flex-start" }}
                                                            padding="none"
                                                            className='cl-global-col'
                                                            align="left"
                                                            style={{ width: "200px", position: "relative", alignContent: "flex-start" }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    flexDirection: "row",
                                                                    borderRadius: "6px",
                                                                    width: "168px",
                                                                    height: "32px",
                                                                    padding: "5px",
                                                                    gap: "5px",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: "18px",
                                                                        height: "18px",
                                                                        borderRadius: "2px",
                                                                        backgroundColor: row.color,
                                                                        border: "1px solid #E5E8E8",
                                                                    }}
                                                                ></div>
                                                                <span className='cl-rating-color-col-text' >{row.color}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{ padding: "0px 0px 0px 15px", alignContent: "center" }}
                                                            padding="none"
                                                            className='cl-rating-col'
                                                            align="left"
                                                        >
                                                            <span className='times-used-text'>{row?.comments_count}</span>
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            width="160px"
                                                            sx={{ position: "relative", alignContent: "center" }}
                                                        >
                                                            <CLTagsButtonGroup
                                                                isHTCompany={isHTCompany}
                                                                handleOpenConfirmationModal={handleOpenConfirmationModal}
                                                                row={row}
                                                                handleOpenAddEditRating={handleOpenAddEditRating}
                                                                setDeletedTagId={setDeletedTagId}
                                                                deletedTagId={deletedTagId}
                                                                name={"rating"}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                        )}
                                                    </Draggable>
                                                );
                                            }) : <TableRow style={{ height: "100%" }}>
                                                <TableCell colSpan={999} style={{ padding: 0 }}>
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        height="100%"
                                                        minHeight="360px"
                                                        textAlign="center"
                                                    >
                                                        <Typography className="cl-empty-text">No ratings found</Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>}
                                            {provided.placeholder}
                                        </TableBody>
                                    )}
                                </StrictModeDroppable>
                            </DragDropContext>
                        </Table>
                    </TableContainer>
                </Paper>
                <CLDeleteRatings
                    setDeleteLoader={setDeleteLoader}
                    deleteLoader={deleteLoader}
                    deleteConfirmationModal={deleteConfirmationModal}
                    handleCloseConfirmationModal={handleCloseConfirmationModal}
                    deletedTagId={deletedTagId}
                    setDeletedTagId={setDeletedTagId}
                    isHTCompany={isHTCompany}
                    fetchRatings={fetchRatings}
                    isGlobalRatingEnabled={isGlobalRatingEnabled}
                />
            </Box>
        </>
    )
}

export default CLRating;